import { useState } from "react";
import { useSignupForm } from "../hooks/useSignupForm";
import { useUpcomingWorkshops, type WorkshopEvent } from "../hooks/useUpcomingWorkshops";
import { EventCalendarGrid } from "./EventCalendarGrid";

const DATE_FORMATTER = new Intl.DateTimeFormat("nl-BE", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

function formatEventLine(ev: WorkshopEvent): string {
  const d = new Date(`${ev.datum}T12:00:00`);
  const date = Number.isNaN(d.getTime()) ? ev.datum : DATE_FORMATTER.format(d);
  const naam = ev.workshop.naam || ev.naam;
  const loc = ev.locatie.naam ? ` · ${ev.locatie.naam}` : "";
  return `${date} — ${naam}${loc}`;
}

export function CTA() {
  const wsState = useUpcomingWorkshops();
  const events = wsState.status === "ok" ? wsState.events : [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { handleSubmit, status, message, isSubmitting } = useSignupForm("kids-signup", {
    onSuccess: () => setSelectedId(null),
  });

  const selectedEvent = events.find((e) => e.id === selectedId) ?? null;
  const workshopValue = selectedEvent
    ? selectedEvent.workshop.naam || selectedEvent.naam
    : "wachtlijst";

  const isDev = import.meta.env.DEV;
  const dev = isDev
    ? {
        parent: "Test Ouder",
        email: "tuur.vanholen@outlook.com",
        phone: "+32 470 12 34 56",
        child: "Lize",
        age: "11",
        school: "6de",
        experience: "wat",
        notes: "Geen allergieën — testinschrijving.",
      }
    : null;

  const isLoading = wsState.status === "loading";
  const hasEvents = events.length > 0;
  const heading = hasEvents ? "Schrijf je in" : "Binnenkort nieuwe workshops";
  const subcopy = hasEvents
    ? "Kies een datum hieronder of laat je gegevens achter voor de wachtlijst. €30 all-in (pilot-prijs), je kind bouwt een werkende memory-game in 3 uur (max 8 plaatsen)."
    : "Op dit moment staat er nog geen workshop ingepland. Laat je gegevens achter — we sturen je een mail zodra een nieuwe datum vastligt.";

  return (
    <section id="inschrijven">
      <div className="container">
        <div className="cta-final">
          <div className="cta-final-inner">
            <h2>{heading}</h2>
            <p>{subcopy}</p>
            {hasEvents && (
              <div className="cta-cal-shell">
                <div className="form-section-label cta-cal-label">Welke datum?</div>
                <EventCalendarGrid
                  events={events}
                  selectedId={selectedId}
                  onSelect={(ev) => setSelectedId((cur) => (cur === ev.id ? null : ev.id))}
                  variant="dark"
                  busy={isLoading}
                />
                <div className="cta-cal-status">
                  {selectedEvent ? (
                    <>
                      <strong>Gekozen:</strong> {formatEventLine(selectedEvent)}{" "}
                      <button
                        type="button"
                        className="cta-cal-clear"
                        onClick={() => setSelectedId(null)}
                      >
                        wissen
                      </button>
                    </>
                  ) : (
                    "Geen datum gekozen — je komt op de wachtlijst."
                  )}
                </div>
              </div>
            )}

            <form className="signup-form signup-form-light" onSubmit={handleSubmit} noValidate>
              <input type="hidden" name="workshop" value={workshopValue} />
              {selectedEvent && (
                <>
                  <input type="hidden" name="workshop_event_id" value={selectedEvent.id} />
                  <input type="hidden" name="workshop_event_date" value={selectedEvent.datum} />
                  <input type="hidden" name="workshop_event_location" value={selectedEvent.locatie.naam || ""} />
                </>
              )}

              <div className="form-section-label">Over de ouder</div>
              <label className="sr-only" htmlFor="kdParent">Naam ouder</label>
              <input
                type="text"
                id="kdParent"
                name="parent"
                placeholder="Naam ouder"
                autoComplete="name"
                required
                defaultValue={dev?.parent}
              />
              <label className="sr-only" htmlFor="kdEmail">Email</label>
              <input
                type="email"
                id="kdEmail"
                name="email"
                placeholder="jouw@email.be"
                autoComplete="email"
                required
                defaultValue={dev?.email}
              />
              <label className="sr-only" htmlFor="kdPhone">Telefoon</label>
              <input
                type="tel"
                id="kdPhone"
                name="phone"
                placeholder="Telefoonnummer (bereikbaar tijdens workshop)"
                autoComplete="tel"
                defaultValue={dev?.phone}
              />

              <div className="form-section-label">Over het kind</div>
              <label className="sr-only" htmlFor="kdChildName">Voornaam kind</label>
              <input
                type="text"
                id="kdChildName"
                name="child_name"
                placeholder="Voornaam kind"
                autoComplete="off"
                required
                defaultValue={dev?.child}
              />
              <label className="sr-only" htmlFor="kdChildAge">Leeftijd kind</label>
              <input
                type="text"
                id="kdChildAge"
                name="child_age"
                placeholder="Leeftijd (10-12)"
                inputMode="numeric"
                required
                defaultValue={dev?.age}
              />
              <label className="sr-only" htmlFor="kdSchool">Klas / school</label>
              <select id="kdSchool" name="school_year" defaultValue={dev?.school ?? ""}>
                <option value="">Klas</option>
                <option value="5de">5de leerjaar</option>
                <option value="6de">6de leerjaar</option>
                <option value="1mid">1ste middelbaar</option>
                <option value="ander">Andere</option>
              </select>
              <label className="sr-only" htmlFor="kdExperience">Ervaring</label>
              <select id="kdExperience" name="experience" defaultValue={dev?.experience ?? ""}>
                <option value="">Ervaring met coderen / Arduino?</option>
                <option value="geen">Geen ervaring — absolute beginner</option>
                <option value="wat">Al wat geknutseld (Scratch, kit, ...)</option>
                <option value="ervaren">Kan al coderen / Arduino-basis</option>
              </select>
              <label className="signup-checkbox">
                <input
                  type="checkbox"
                  name="has_kit"
                  value="yes"
                  defaultChecked={false}
                />
                <span>Mijn kind heeft al een Arduino / bouw kit thuis</span>
              </label>
              <label className="sr-only" htmlFor="kdNotes">Opmerkingen</label>
              <textarea
                id="kdNotes"
                name="notes"
                placeholder="Allergieën, medische info of iets dat we moeten weten (optioneel)"
                rows={3}
                defaultValue={dev?.notes}
              ></textarea>

              <button
                type="submit"
                className="btn btn-white btn-lg"
                disabled={isSubmitting || status === "ok"}
                aria-busy={isSubmitting}
              >
                {isSubmitting
                  ? "Bezig…"
                  : status === "ok"
                    ? "Verzonden ✓"
                    : selectedEvent
                      ? "Schrijf in →"
                      : "Houd me op de hoogte →"}
              </button>
              <div className={`signup-msg ${status === "ok" ? "ok" : status === "err" ? "err" : ""}`}>
                {message}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
